import { LogIn, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import OtpResumeLogin from "@/D2C/features/auth/components/otp/OtpResumeLogin";
import { App_Url_New } from "@/shared/common/static";
import { AvatarButton } from "@/shared/components/AvatarButton";
import CommonLogoutModal from "@/shared/components/CommonLogoutModal";
import { MenuPanel } from "@/shared/components/MenuPanel";
import { closeModal, openModal } from "@/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";

type MenuAction = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
};

type HeaderAuthControlProps = {
  className?: string;
  loginButtonClassName?: string;
  extraActions?: MenuAction[];
  dashboardUrl?: string;
  showDashboard?: boolean;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
};

export default function HeaderAuthControl({
  className,
  loginButtonClassName,
  extraActions = [],
}: HeaderAuthControlProps) {
  const dispatch = useDispatch();
  const {
    auth: { customerAuth },
  } = usePosterReducers();

  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuPanelRef = useRef<HTMLDivElement | null>(null);

  const displayName =
    `${customerAuth?.client?.first_name || ""} ${customerAuth?.client?.last_name || ""}`.trim();
  const initials = getInitials(displayName || "Account");

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (!menuOpen || !menuPanelRef.current) return;

    menuPanelRef.current.animate(
      [
        { opacity: 0, transform: "translateY(-8px) scale(0.96)" },
        { opacity: 1, transform: "translateY(0) scale(1)" },
      ],
      {
        duration: 180,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      }
    );
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.LOGOUT_CONFIRM,
        title: "Logout?",
        className: "max-w-md",
        children: (
          <CommonLogoutModal displayName={displayName} redirectTo={App_Url_New.D2CPageUrl.home} />
        ),
      })
    );
  };

  const menuActions: MenuAction[] = [
    ...extraActions,
    {
      label: "Logout",
      icon: <LogOut className="h-4 w-4" />,
      onClick: handleLogout,
      className:
        "w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-theme-danger hover:bg-theme-danger-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
    },
  ];

  const handleLoginClick = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.AUTH_RETAIL_LOGIN,
        className: "max-w-lg",
        children: (
          <OtpResumeLogin
            isOpen
            onClose={() => dispatch(closeModal())}
            showUserLoginForm
            validationType="auto"
            autoRedirect={false}
            renderInline
          />
        ),
      })
    );
  };

  if (!customerAuth?.access_token) {
    if (location.pathname.includes("policy")) {
      return null;
    }

    return (
      <div className={className}>
        <button
          type="button"
          onClick={handleLoginClick}
          className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 font-semibold leading-none transition-colors ${
            loginButtonClassName || "bg-theme-accent text-theme-text hover:bg-theme-accent-hover"
          }`}
        >
          <span>Login</span>
          <LogIn size={18} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className ?? ""}`} ref={menuRef}>
      <AvatarButton
        initials={initials}
        menuOpen={menuOpen}
        onClick={() => setMenuOpen((prev) => !prev)}
      />

      {menuOpen ? (
        <MenuPanel panelRef={menuPanelRef} displayName={displayName} actions={menuActions} />
      ) : null}
    </div>
  );
}
